import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import api from '../services/api';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated } = useSelector(state => state.auth);

  // Avoid refetching permissions repeatedly for the same role
  const lastFetchedRoleRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      const role = (user.role || '').trim().toLowerCase();
      if (lastFetchedRoleRef.current !== role) {
        lastFetchedRoleRef.current = role;
        fetchUserPermissions();
      } else {
        // Already have permissions for this role
        setLoading(false);
      }
    } else {
      lastFetchedRoleRef.current = null;
      setPermissions({});
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const fetchUserPermissions = async () => {
    try {
      setLoading(true);
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[PermissionContext] Fetching permissions for role:', user?.role);
      }
      
      const response = await api.get(`/role-permissions/role/${user.role}`);
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[PermissionContext] Permissions API response count:', Array.isArray(response.data) ? response.data.length : 'n/a');
      }
      
      // Convert array to object for easier lookup
      const permissionsMap = {};
      response.data.forEach(perm => {
        permissionsMap[perm.page_key] = {
          can_access: perm.can_access,
          can_create: perm.can_create,
          can_read: perm.can_read,
          can_update: perm.can_update,
          can_delete: perm.can_delete
        };
      });
      
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[PermissionContext] Processed permissions keys:', Object.keys(permissionsMap));
        // eslint-disable-next-line no-console
        console.debug('[PermissionContext] quote-management permission:', permissionsMap['quote-management']);
      }
      setPermissions(permissionsMap);
    } catch (error) {
      console.error('[PermissionContext] Error fetching user permissions:', error);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  };

  const memoRole = useMemo(() => (user?.role || '').trim().toLowerCase(), [user?.role]);

  const hasPermission = useCallback((pageKey, action = 'can_access') => {
    // Admin always has all permissions
    if (memoRole === 'admin' || memoRole === 'administrator') {
      return true;
    }

    // Customer-type roles should never have admin access (except for feature permissions)
    if (['customer', 'vip', 'cus_nags', 'the_bee'].includes(memoRole)) {
      // Allow feature.* permissions to be checked normally
      if (pageKey.startsWith('feature.')) {
        const pagePermissions = permissions[pageKey];
        return pagePermissions ? pagePermissions[action] === true : false;
      }
      return false;
    }

    const pagePermissions = permissions[pageKey];
    if (!pagePermissions) {
      if (process.env.NODE_ENV !== 'production' && pageKey === 'quote-management') {
        // eslint-disable-next-line no-console
        console.debug('[PermissionContext] hasPermission:', { pageKey, action, memoRole, result: false, reason: 'no pagePermissions' });
      }
      return false;
    }

    const allowed = pagePermissions[action] === true;
    if (process.env.NODE_ENV !== 'production' && pageKey === 'quote-management') {
      // eslint-disable-next-line no-console
      console.debug('[PermissionContext] hasPermission:', { pageKey, action, memoRole, allowed, perms: pagePermissions });
    }
    return allowed;
  }, [memoRole, permissions]);

  const hasAnyAdminAccess = useCallback(() => {
    // Customer-type roles should never have admin access
    if (['customer', 'vip', 'cus_nags', 'the_bee'].includes(memoRole)) {
      return false;
    }

    // Admin always has access
    if (memoRole === 'admin' || memoRole === 'administrator') {
      return true;
    }

    // Check if user has access to any admin page
    return Object.values(permissions).some(perm => perm.can_access === true);
  }, [memoRole, permissions]);

  const canAccessAdminDashboard = useCallback(() => hasPermission('dashboard', 'can_access'), [hasPermission]);

  const getAccessiblePages = useCallback(() => {
    return Object.entries(permissions)
      .filter(([_, perms]) => perms.can_access)
      .map(([pageKey, perms]) => ({ pageKey, ...perms }));
  }, [permissions]);

  const value = {
    permissions,
    loading,
    hasPermission,
    hasAnyAdminAccess,
    canAccessAdminDashboard,
    getAccessiblePages,
    refreshPermissions: fetchUserPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
